/** @file The live-sync session of the open project (null when no project is live). */
import type { Id } from '@pose2d/format/types';
import type { PendingBatch } from '../../stores/liveStore';
import type { ApiClient } from '../createApiClient';
import type { LiveSocket } from '../live/openLiveSocket';

/** What sync needs from the REST client (fakes in tests). */
export type SyncClient = Pick<ApiClient, 'getDoc' | 'getChanges' | 'applyCommands' | 'revert'>;

/** Per-project sync state outside the stores (the stores hold the doc, batches, presence and the feed). */
export interface SyncSession {
  readonly projectId: string;
  readonly actor: string;
  readonly client: SyncClient;
  /** Sends a frame while the socket is open; false otherwise. */
  readonly send: LiveSocket['send'];
  /** Highest rev known to exist (hello, frames, pong, REST answers). */
  knownRev: number;
  /** The running sync loop (kickSync), if any. */
  busy: Promise<void> | null;
  /** Tail of the serial send queue (E8.5); never rejects. */
  queue: Promise<void>;
  nextSeq: number;
  /** name → id created by each batch's optimistic apply, by batch seq, until its answer (E8.5). */
  created: Map<number, Record<string, Id>>;
  socketOpen: boolean;
  /** The batches not yet answered when stop ran, still sent in order (null while the session is live). */
  afterStop: PendingBatch[] | null;
  resyncWanted: boolean;
  hashDue: boolean;
}

let current: SyncSession | null = null;

/** Access to the open project's session; startLiveSync sets it and its stop clears it. */
export const liveSession = {
  /** The session, or null when no project is live. */
  get: (): SyncSession | null => current,
  /** Replaces the session (null on stop). */
  set: (session: SyncSession | null): void => {
    current = session;
  },
};
